// src/components/TrainingKitActivities.js
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

// Composant pour un Quiz
export const QuizActivity = ({ activity, onComplete, learningStyle }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null); // Timer pour les niveaux intermédiaire et avancé
  const [isCompleted, setIsCompleted] = useState(false);
  const [feedback, setFeedback] = useState([]);

  const currentQuestion = activity.questions[currentQuestionIndex];

  // Ajuster la difficulté en fonction du niveau
  useEffect(() => {
    if (activity.difficulty === "intermediate" || activity.difficulty === "advanced") {
      setTimeLeft(30); // 30 secondes par question pour les niveaux intermédiaire et avancé
    }
  }, [currentQuestionIndex, activity.difficulty]);

  // Gérer le timer
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleAnswer(); // Passer à la question suivante si le temps est écoulé
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleAnswer = () => {
    let isCorrect = false;
    if (activity.difficulty === "advanced") {
      // Pour le niveau avancé, la réponse est ouverte (comparaison insensible à la casse)
      isCorrect = selectedAnswer?.toLowerCase() === currentQuestion.correctAnswer.toLowerCase();
    } else {
      // Pour débutant et intermédiaire, comparaison directe
      isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    }

    if (isCorrect) {
      setScore(score + 1);
      toast.success("Bonne réponse !");
      setFeedback([...feedback, { question: currentQuestion.question, correct: true, userAnswer: selectedAnswer }]);
    } else {
      toast.error(`Mauvaise réponse. La bonne réponse était : ${currentQuestion.correctAnswer}`);
      setFeedback([...feedback, { question: currentQuestion.question, correct: false, userAnswer: selectedAnswer, correctAnswer: currentQuestion.correctAnswer }]);
    }

    if (currentQuestionIndex < activity.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      if (activity.difficulty !== "beginner") setTimeLeft(30); // Réinitialiser le timer
    } else {
      const finalScore = score + (isCorrect ? 1 : 0);
      toast.info(`Quiz terminé ! Votre score : ${finalScore}/${activity.questions.length}`);
      setIsCompleted(true);
    }
  };

  const getFeedbackMessage = () => {
    const finalScore = score + (selectedAnswer === currentQuestion.correctAnswer ? 1 : 0);
    if (finalScore === activity.questions.length) {
      return "Excellent travail ! Tu as tout juste. Continue à t’entraîner pour maintenir ce niveau.";
    } else if (finalScore >= activity.questions.length / 2) {
      return "Bon travail ! Tu as bien compris les bases. Concentre-toi sur les détails pour t’améliorer.";
    } else {
      return "Il y a encore du travail à faire. Revois les concepts de base et entraîne-toi davantage.";
    }
  };

  if (isCompleted) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Résultat du Quiz</h3>
        <p className="text-gray-600 dark:text-gray-300">
          Score final : {score}/{activity.questions.length}
        </p>
        <p className="text-gray-600 dark:text-gray-300">{getFeedbackMessage()}</p>
        <h4 className="text-md font-semibold">Détails des réponses :</h4>
        <ul className="space-y-2">
          {feedback.map((item, index) => (
            <li key={index} className={item.correct ? "text-green-500" : "text-red-500"}>
              <p><strong>Question :</strong> {item.question}</p>
              <p><strong>Ta réponse :</strong> {item.userAnswer}</p>
              {!item.correct && <p><strong>Bonne réponse :</strong> {item.correctAnswer}</p>}
            </li>
          ))}
        </ul>
        <button
          onClick={onComplete}
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
        >
          Terminer
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-4"
    >
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Question {currentQuestionIndex + 1}/{activity.questions.length}
      </p>
      {timeLeft !== null && (
        <p className="text-sm text-red-500">Temps restant : {timeLeft} secondes</p>
      )}
      <h3 className="text-lg font-medium">{currentQuestion.question}</h3>
      {learningStyle === "visuel" && currentQuestion.image && (
        <img src={currentQuestion.image} alt="Illustration" className="w-full h-32 object-cover rounded-lg mb-4" />
      )}
      {learningStyle === "auditif" && currentQuestion.explanation && (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{currentQuestion.explanation}</p>
      )}
      {activity.difficulty === "advanced" ? (
        <input
          type="text"
          value={selectedAnswer || ""}
          onChange={(e) => setSelectedAnswer(e.target.value)}
          placeholder="Tapez votre réponse..."
          className="w-full p-2 rounded-lg bg-gray-100 dark:bg-gray-700 border-none focus:ring-2 focus:ring-blue-500"
        />
      ) : (
        <div className="space-y-2">
          {currentQuestion.options.map((option, index) => (
            <motion.button
              key={index}
              onClick={() => setSelectedAnswer(option)}
              whileHover={{ scale: 1.05 }}
              className={`w-full p-2 rounded-lg text-left ${
                selectedAnswer === option ? "bg-blue-500 text-white" : "bg-gray-200 dark:bg-gray-600"
              }`}
            >
              {option}
            </motion.button>
          ))}
        </div>
      )}
      <motion.button
        onClick={handleAnswer}
        disabled={!selectedAnswer}
        whileHover={{ scale: 1.05 }}
        className="mt-4 bg-green-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
      >
        Valider
      </motion.button>
    </motion.div>
  );
};

// Composant pour une Analyse de visuel
export const VisualAnalysisActivity = ({ activity, onComplete, learningStyle }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [feedback, setFeedback] = useState([]);

  const currentQuestion = activity.questions[currentQuestionIndex];

  useEffect(() => {
    if (activity.difficulty === "intermediate" || activity.difficulty === "advanced") {
      setTimeLeft(45); // 45 secondes pour analyser une image
    }
  }, [currentQuestionIndex, activity.difficulty]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleAnswer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleAnswer = () => {
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    if (isCorrect) {
      setScore(score + 1);
      toast.success("Bonne réponse !");
      setFeedback([...feedback, { question: currentQuestion.question, correct: true, userAnswer: selectedAnswer }]);
    } else {
      toast.error(`Mauvaise réponse. La bonne réponse était : ${currentQuestion.correctAnswer}`);
      setFeedback([...feedback, { question: currentQuestion.question, correct: false, userAnswer: selectedAnswer, correctAnswer: currentQuestion.correctAnswer }]);
    }

    if (currentQuestionIndex < activity.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      if (activity.difficulty !== "beginner") setTimeLeft(45);
    } else {
      const finalScore = score + (isCorrect ? 1 : 0);
      toast.info(`Analyse terminée ! Votre score : ${finalScore}/${activity.questions.length}`);
      setIsCompleted(true);
    }
  };

  const getFeedbackMessage = () => {
    const finalScore = score + (selectedAnswer === currentQuestion.correctAnswer ? 1 : 0);
    if (finalScore === activity.questions.length) {
      return "Excellent ! Tu as un œil aiguisé pour les détails visuels. Continue comme ça !";
    } else if (finalScore >= activity.questions.length / 2) {
      return "Bien joué ! Travaille sur l’identification des détails clés pour t’améliorer.";
    } else {
      return "Il faut plus d’entraînement. Prends le temps d’analyser chaque élément de l’image.";
    }
  };

  if (isCompleted) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Résultat de l’Analyse Visuelle</h3>
        <p className="text-gray-600 dark:text-gray-300">
          Score final : {score}/{activity.questions.length}
        </p>
        <p className="text-gray-600 dark:text-gray-300">{getFeedbackMessage()}</p>
        <h4 className="text-md font-semibold">Détails des réponses :</h4>
        <ul className="space-y-2">
          {feedback.map((item, index) => (
            <li key={index} className={item.correct ? "text-green-500" : "text-red-500"}>
              <p><strong>Question :</strong> {item.question}</p>
              <p><strong>Ta réponse :</strong> {item.userAnswer}</p>
              {!item.correct && <p><strong>Bonne réponse :</strong> {item.correctAnswer}</p>}
            </li>
          ))}
        </ul>
        <button
          onClick={onComplete}
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
        >
          Terminer
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-4"
    >
      <img src={activity.visualUrl} alt="Visuel à analyser" className="w-full h-64 object-contain rounded-lg" />
      {timeLeft !== null && (
        <p className="text-sm text-red-500">Temps restant : {timeLeft} secondes</p>
      )}
      <h3 className="text-lg font-medium">{currentQuestion.question}</h3>
      {learningStyle === "auditif" && currentQuestion.explanation && (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{currentQuestion.explanation}</p>
      )}
      <div className="space-y-2">
        {currentQuestion.options.map((option, index) => (
          <motion.button
            key={index}
            onClick={() => setSelectedAnswer(option)}
            whileHover={{ scale: 1.05 }}
            className={`w-full p-2 rounded-lg text-left ${
              selectedAnswer === option ? "bg-blue-500 text-white" : "bg-gray-200 dark:bg-gray-600"
            }`}
          >
            {option}
          </motion.button>
        ))}
      </div>
      <motion.button
        onClick={handleAnswer}
        disabled={!selectedAnswer}
        whileHover={{ scale: 1.05 }}
        className="mt-4 bg-green-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
      >
        Valider
      </motion.button>
    </motion.div>
  );
};

// Composant pour une Énigme
export const EnigmaActivity = ({ activity, onComplete, learningStyle }) => {
  const [userAnswer, setUserAnswer] = useState("");
  const [showHint, setShowHint] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (activity.difficulty === "intermediate") {
      setTimeLeft(60); // 1 minute pour résoudre l’énigme
    } else if (activity.difficulty === "advanced") {
      setTimeLeft(120); // 2 minutes pour une énigme avancée
    }
  }, [activity.difficulty]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          toast.error("Temps écoulé ! Essaye une autre énigme.");
          setIsCompleted(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleSubmit = () => {
    setAttempts(attempts + 1);
    if (userAnswer.toLowerCase() === activity.correctAnswer.toLowerCase()) {
      toast.success("Énigme résolue !");
      setIsCompleted(true);
    } else {
      toast.error("Mauvaise réponse. Essayez encore !");
      if (attempts >= 2 && !showHint) {
        setShowHint(true);
        toast.info("Un indice a été affiché pour t’aider.");
      }
    }
  };

  if (isCompleted) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Résultat de l’Énigme</h3>
        <p className="text-gray-600 dark:text-gray-300">
          {userAnswer.toLowerCase() === activity.correctAnswer.toLowerCase()
            ? "Félicitations, tu as résolu l’énigme !"
            : "Temps écoulé ou énigme non résolue."}
        </p>
        <p className="text-gray-600 dark:text-gray-300">Nombre de tentatives : {attempts}</p>
        <p className="text-gray-600 dark:text-gray-300">
          Conseil : {attempts > 3 ? "Prends le temps de lire attentivement le scénario et les indices." : "Bien joué ! Continue à résoudre des énigmes pour t’améliorer."}
        </p>
        <button
          onClick={onComplete}
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
        >
          Terminer
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-4"
    >
      <p className="text-gray-600 dark:text-gray-300">{activity.scenario}</p>
      {learningStyle === "visuel" && activity.image && (
        <img src={activity.image} alt="Illustration de l’énigme" className="w-full h-32 object-cover rounded-lg mb-4" />
      )}
      {learningStyle === "auditif" && activity.explanation && (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{activity.explanation}</p>
      )}
      {timeLeft !== null && (
        <p className="text-sm text-red-500">Temps restant : {timeLeft} secondes</p>
      )}
      <input
        type="text"
        value={userAnswer}
        onChange={(e) => setUserAnswer(e.target.value)}
        placeholder="Votre réponse..."
        className="w-full p-2 rounded-lg bg-gray-100 dark:bg-gray-700 border-none focus:ring-2 focus:ring-blue-500"
      />
      <motion.button
        onClick={() => setShowHint(!showHint)}
        whileHover={{ scale: 1.05 }}
        className="text-blue-500 hover:underline"
      >
        {showHint ? "Cacher les indices" : "Afficher un indice"}
      </motion.button>
      {showHint && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="text-sm text-gray-500 dark:text-gray-400"
        >
          {activity.hints.map((hint, index) => (
            <p key={index}>- {hint}</p>
          ))}
        </motion.div>
      )}
      <motion.button
        onClick={handleSubmit}
        whileHover={{ scale: 1.05 }}
        className="mt-4 bg-green-500 text-white px-4 py-2 rounded"
      >
        Soumettre
      </motion.button>
    </motion.div>
  );
};